/**
 * US privacy opt-out (CCPA / CPRA + similar state laws).
 *
 * The site is US-only, so we do NOT gate tracking on prior consent.
 * Instead we honor an opt-out signal from either:
 *   1. The "Your Privacy Choices" button (localStorage flag), or
 *   2. The browser-level Global Privacy Control (GPC) signal.
 *
 * When opted out, ad pixels switch to Limited Data Use / consent-denied mode
 * and server-side CAPI calls strip user-level identifiers.
 */

const OPT_OUT_KEY = "oaria-privacy-optout";
const OPT_OUT_EVENT = "oaria-privacy-optout-change";

export function gpcEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

export function manualOptOut(): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function hasOptedOut(): boolean {
  return manualOptOut() || gpcEnabled();
}

export function setOptOut(value: boolean) {
  if (typeof localStorage === "undefined") return;
  try {
    if (value) {
      localStorage.setItem(OPT_OUT_KEY, "1");
    } else {
      localStorage.removeItem(OPT_OUT_KEY);
    }
    window.dispatchEvent(new CustomEvent(OPT_OUT_EVENT, { detail: { value } }));
  } catch {
    /* ignore */
  }
}

export function onOptOutChange(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const cb = () => handler();
  window.addEventListener(OPT_OUT_EVENT, cb);
  return () => window.removeEventListener(OPT_OUT_EVENT, cb);
}
